import { LOCALE, LOCALES } from '@shared/utils/i18n/locales';
import { normalizeOsvMoney } from './osvMoney';

/*
 * OSI-808: расчёт итогов ОСВ в разрезе КАТЕГОРИЙ услуг.
 *
 * Зачем: в таблице ОСВ (кабинет председателя и кабинет жителя) нужен блок итогов —
 * общее «ИТОГО» и по одной строке на каждую категорию услуг, с шестью денежными
 * колонками. Аналог уже есть в excel-отчёте «Общая ведомость по сальдо»
 * (oci-docs/report/excel/saldo_xls.go:380-412), где суммы копятся в map по имени услуги.
 *
 * Ключевой факт (проверен по бэкенду): поле `serviceName` в ответе ОСВ — это НЕ название
 * услуги, а название ГРУППЫ услуг, т.е. КАТЕГОРИИ (service_groups.name_ru).
 * См. OSI.Core/OSI.Core/Logic/OSVLogic.cs:522 и :558 — выборка группируется по
 * `t.GroupId` + `t.Group.NameRu`, а не по услуге; модель — OSI.Core.Types/Models/Reports/
 * OSVAbonent.cs:31-35. Поэтому строки дерева с level: 1 — это УЖЕ готовые категории,
 * и разбивка получается их простой свёрткой по serviceName/serviceNameKz.
 * Бэкенд ради этой задачи НЕ меняем.
 *
 * Набор категорий НЕ фиксирован: помимо системных («Текущие взносы…», «Накопительные
 * взносы на капремонт», «Целевой взнос», «Дополнительные услуги») бывают «Услуги паркинга»,
 * «Кладовки», капремонт паркинга/кладовок и собственные группы конкретного ОСИ —
 * функция обязана выдерживать любое их количество.
 *
 * Функция работает с УЖЕ построенными строками грида, поэтому подходит обоим построителям:
 *  - widgets/osi/osv/model/ViewModel.ts:196 (кабинет председателя, строки типизированы + есть id);
 *  - shared/utils/helpers/osv.ts:7 (кабинет жителя, строки нетипизированы, id нет).
 * Отсюда `any` во ВХОДНЫХ строках — код кабинета жителя не типизирован. Результат типизирован строго.
 */

// Шесть денежных показателей ОСВ. Имена полей совпадают с полями строк грида
// (totalBegin/totalDebet/totalFixes/totalKredit/totalFine/totalEnd), чтобы блок итогов
// можно было отрисовать теми же колонками, что и сама таблица, без маппинга имён.
export type OsvMoneyTotals = {
  /** Сальдо на начало */
  totalBegin: number;
  /** Начислено */
  totalDebet: number;
  /** Корректировки */
  totalFixes: number;
  /** Оплачено */
  totalKredit: number;
  /** Пеня */
  totalFine: number;
  /** Долг (сальдо на конец) */
  totalEnd: number;
};

/** Итог по одной категории услуг (группе услуг). */
export type OsvCategoryTotal = OsvMoneyTotals & {
  /** Название категории на выбранном языке — тем же полем, которым оно показано в дереве */
  category: string;
};

/** Результат расчёта: строки по категориям (в порядке первого появления) + общий итог. */
export type OsvCategoryTotalsResult = {
  categories: OsvCategoryTotal[];
  total: OsvMoneyTotals;
};

// Поля строки, по которым идёт суммирование. Берём именно продублированные `total*`,
// а не исходные (begin/sumOfAccurals/...): `total*` присутствуют у строк level: 1 в ОБОИХ
// построителях, и это ровно те значения, которые видит пользователь в ячейках грида.
const MONEY_FIELDS: (keyof OsvMoneyTotals)[] = [
  'totalBegin',
  'totalDebet',
  'totalFixes',
  'totalKredit',
  'totalFine',
  'totalEnd'
];

const emptyTotals = (): OsvMoneyTotals => ({
  totalBegin: 0,
  totalDebet: 0,
  totalFixes: 0,
  totalKredit: 0,
  totalFine: 0,
  totalEnd: 0
});

// Устойчивое приведение к числу БЕЗ округления: отсутствующие/нечисловые поля дают 0.
// Округление здесь намеренно не применяется — см. комментарий про нормализацию ниже.
const toAmount = (value: unknown): number => (typeof value === 'number' && !Number.isNaN(value) ? value : 0);

/**
 * Считает итоги ОСВ по категориям услуг.
 *
 * @param rows строки, построенные getOsvRows (любой из двух реализаций)
 * @param langKey текущая локаль (та же, что передаётся в getOsvRows)
 */
export function getOsvCategoryTotals(rows: any[], langKey: LOCALE): OsvCategoryTotalsResult {
  // Название категории берём ТЕМ ЖЕ полем, которым оно отображается в дереве грида
  // (см. getOsvRows: flat: [..., e?.[translatedField]]), чтобы подписи блока итогов
  // и веток дерева всегда совпадали.
  const translatedField = langKey === LOCALES.RU ? 'serviceName' : 'serviceNameKz';

  // Map, а не обычный объект: Map гарантирует порядок вставки для ЛЮБЫХ строковых ключей,
  // включая числоподобные («2024»), которые обычный объект переупорядочил бы в начало.
  const acc = new Map<string, OsvCategoryTotal>();

  // Устойчивость: rows может прийти undefined/null (первый рендер до загрузки, ошибка запроса).
  const safeRows = Array.isArray(rows) ? rows : [];

  safeRows.forEach((row) => {
    // Сворачиваем ТОЛЬКО строки услуг (level 1). Строки level 0 — это помещения,
    // они уже содержат сумму по своим услугам, и их учёт дал бы двойной счёт.
    if (row?.level !== 1) return;

    // Fallback на русское название: у собственных групп конкретного ОСИ казахское имя
    // бывает пустым. Без fallback все такие категории схлопнулись бы в одну безымянную строку.
    // Если названия нет вовсе (порченые данные) — строка идёт в безымянную категорию '',
    // а не отбрасывается: иначе «ИТОГО» разошлось бы с итогом грида на эту сумму.
    const category: string = row?.[translatedField] || row?.serviceName || '';

    const existing = acc.get(category);
    const current: OsvCategoryTotal = existing ?? { category, ...emptyTotals() };
    if (!existing) acc.set(category, current);

    MONEY_FIELDS.forEach((field) => {
      current[field] += toAmount(row?.[field]);
    });
  });

  /*
   * Порядок категорий — по порядку ПЕРВОГО ПОЯВЛЕНИЯ в строках (порядок вставки в Map).
   * Осознанное решение, а не «как получилось»:
   *  - бэкенд отдаёт услуги абонента уже отсортированными (OSVAbonent.cs:31 — OrderBy(ServiceGroupId)),
   *    т.е. внутри абонента порядок категорий стабилен и осмысленный (системные группы идут первыми);
   *  - excel-отчёт «Общая ведомость по сальдо» порядок не гарантирует ВООБЩЕ (итерация по Go-map),
   *    поэтому копировать его поведение нельзя;
   *  - любая своя сортировка (по алфавиту/по сумме) — это новая, не согласованная с бэкендом
   *    договорённость; алфавит к тому же разъехался бы между ru и kk.
   * Итог: «порядок первого появления» — единственный детерминированный вариант,
   * не требующий изменения контракта API.
   */
  // Нормализация (округление до 2 знаков + снятие «-0») применяется ОДИН раз — на готовой сумме,
  // а не к каждому слагаемому. Причина: домен денег — 2 знака (как в excel-своде oci-docs),
  // а накопление float64 даёт артефакты вида -3.47e-18 (см. комментарий в osvMoney.ts).
  // Округлять слагаемые нельзя — это внесло бы собственную ошибку в сумму;
  // округлять результат нужно — иначе в «ИТОГО» вылезет «-0» или «0.30000000000000004».
  const categories = Array.from(acc.values()).map((item) => {
    const normalized: OsvCategoryTotal = { category: item.category, ...emptyTotals() };
    MONEY_FIELDS.forEach((field) => {
      normalized[field] = normalizeOsvMoney(item[field]);
    });
    return normalized;
  });

  // Общий итог считаем по УЖЕ нормализованным суммам категорий, а не по сырым строкам:
  // так «ИТОГО» в точности равно сумме того, что пользователь видит в строках категорий
  // (иначе расхождение в копейку из-за округления каждой категории по отдельности).
  const total = categories.reduce<OsvMoneyTotals>((sum, item) => {
    MONEY_FIELDS.forEach((field) => {
      sum[field] = normalizeOsvMoney(sum[field] + item[field]);
    });
    return sum;
  }, emptyTotals());

  return { categories, total };
}
